import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Save, FileText, Building2, Eye, EyeOff, Palette, MapPin, Type, Users } from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const DEFAULT_PDF_SETTINGS = {
  company_name: 'Scholze-Kurz & Kurz Immobilien GmbH',
  company_address: '',
  company_phone: '',
  company_email: '',
  company_website: '',
  logo_url: '',
  primary_color: '#2563EB',
  show_map: true,
  map_size: 100,
  show_bewertungsergebnis: true,
  show_gebaeuedaten: true,
  show_gebaeuezustand: true,
  show_modernisierungen: true,
  show_sanierungsbedarf: true,
  show_verwaltungshistorie: true,
  show_verguetungsempfehlung: true,
  show_kontakt: true,
  intro_text: '',
  footer_text: '',
  closing_html: '<p>Vielen Dank für Ihr Interesse an unseren Dienstleistungen. Bei Fragen stehen wir Ihnen gerne zur Verfügung.</p>',
};

const DEFAULT_CUSTOMER_PDF_SETTINGS = {
  ...DEFAULT_PDF_SETTINGS,
  show_verguetungsempfehlung: false,
  closing_html: '<p>Vielen Dank für Ihr Interesse. Diese Analyse dient zur Übersicht der Objektdaten.</p>',
};

export default function AdminPdfSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('standard');
  const [settings, setSettings] = useState(DEFAULT_PDF_SETTINGS);
  const [customerSettings, setCustomerSettings] = useState(DEFAULT_CUSTOMER_PDF_SETTINGS);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin');
      return;
    }
    
    fetch(`${API_URL}/api/settings`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        if (data.pdf_settings) {
          setSettings(prev => ({ ...prev, ...data.pdf_settings }));
        }
        if (data.customer_pdf_settings) {
          setCustomerSettings(prev => ({ ...prev, ...data.customer_pdf_settings }));
        }
      })
      .catch(() => toast.error('Fehler beim Laden der Einstellungen'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const saveSettings = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          pdf_settings: settings,
          customer_pdf_settings: customerSettings
        })
      });
      if (!response.ok) throw new Error('Fehler beim Speichern');
      toast.success('PDF-Einstellungen gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    }
    setSaving(false);
  };

  const updateSetting = (key, value) => {
    if (activeTab === 'customer') {
      setCustomerSettings(prev => ({ ...prev, [key]: value }));
    } else {
      setSettings(prev => ({ ...prev, [key]: value }));
    }
  };

  const currentSettings = activeTab === 'customer' ? customerSettings : settings;

  const sectionToggles = [
    { key: 'show_bewertungsergebnis', label: 'Bewertungsergebnis', description: 'Gesamtbewertung und Bedarfskategorie' },
    { key: 'show_gebaeuedaten', label: 'Objektdaten', description: 'Grundlegende Gebäudeinformationen' },
    { key: 'show_gebaeuezustand', label: 'Gebäudezustand', description: 'Detaillierte Zustandsbewertung' },
    { key: 'show_modernisierungen', label: 'Modernisierungen & Reparaturen', description: 'Durchgeführte Arbeiten' },
    { key: 'show_sanierungsbedarf', label: 'Sanierungsbedarf', description: 'Kommende Sanierungsmaßnahmen' },
    { key: 'show_verwaltungshistorie', label: 'Verwaltungshistorie', description: 'Historische Verwaltungsdaten' },
    { key: 'show_verguetungsempfehlung', label: 'Vergütungsempfehlung', description: 'Berechnete Gebührenempfehlung' },
    { key: 'show_kontakt', label: 'Kontaktdaten', description: 'Ansprechpartner-Informationen' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/einstellungen')} data-testid="back-to-settings">
              <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
            </Button>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-700" />
              <h1 className="text-lg font-semibold">PDF-Einstellungen</h1>
            </div>
          </div>
          <Button onClick={saveSettings} disabled={saving} className="gap-2" data-testid="save-pdf-settings">
            <Save className="h-4 w-4" />
            {saving ? 'Speichern...' : 'Speichern'}
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* PDF Type Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="standard" className="gap-2" data-testid="tab-standard-pdf">
              <FileText className="h-4 w-4" />
              Standard-PDF
            </TabsTrigger>
            <TabsTrigger value="customer" className="gap-2" data-testid="tab-customer-pdf">
              <Users className="h-4 w-4" />
              Kunden-PDF
            </TabsTrigger>
          </TabsList>
          
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            {activeTab === 'standard' ? (
              <p className="text-sm text-blue-800">
                <strong>Standard-PDF:</strong> Vollständiger Report mit allen Preisangaben und Vergütungsempfehlungen. Für interne Verwendung.
              </p>
            ) : (
              <p className="text-sm text-blue-800">
                <strong>Kunden-PDF:</strong> Report ohne Preisangaben - ideal zum Teilen mit Kunden und Interessenten.
              </p>
            )}
          </div>
        </Tabs>

        {/* Company Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Firmeninformationen
            </CardTitle>
            <CardDescription>
              Diese Informationen werden im PDF-Header und Footer verwendet.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Firmenname</Label>
                <Input
                  value={currentSettings.company_name}
                  onChange={(e) => updateSetting('company_name', e.target.value)}
                  placeholder="Musterfirma GmbH"
                  data-testid="pdf-company-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={currentSettings.company_website}
                  onChange={(e) => updateSetting('company_website', e.target.value)}
                  placeholder="www.example.com"
                  data-testid="pdf-company-website"
                />
              </div>
              <div className="space-y-2">
                <Label>E-Mail</Label>
                <Input
                  value={currentSettings.company_email}
                  onChange={(e) => updateSetting('company_email', e.target.value)}
                  placeholder="info@example.com"
                  data-testid="pdf-company-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input
                  value={currentSettings.company_phone}
                  onChange={(e) => updateSetting('company_phone', e.target.value)}
                  placeholder="+49 123 456789"
                  data-testid="pdf-company-phone"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={currentSettings.company_address}
                onChange={(e) => updateSetting('company_address', e.target.value)}
                placeholder="Musterstraße 1, 12345 Musterstadt"
                data-testid="pdf-company-address"
              />
            </div>
          </CardContent>
        </Card>

        {/* Map Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Kartenansicht
            </CardTitle>
            <CardDescription>
              Eine kleine Kartenansicht wird neben der Adresse angezeigt, wenn Koordinaten verfügbar sind.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Karte anzeigen</Label>
                <p className="text-sm text-muted-foreground">Kleine Kartenansicht neben der Adresse im PDF</p>
              </div>
              <Switch
                checked={currentSettings.show_map}
                onCheckedChange={(v) => updateSetting('show_map', v)}
                data-testid="pdf-show-map"
              />
            </div>
            {settings.show_map && (
              <div className="space-y-2">
                <Label>Kartengröße: {settings.map_size}px</Label>
                <Slider
                  value={[currentSettings.map_size]}
                  onValueChange={([v]) => updateSetting('map_size', v)}
                  min={60}
                  max={150}
                  step={10}
                  className="w-full max-w-xs"
                  data-testid="pdf-map-size"
                />
                <p className="text-xs text-muted-foreground">Empfohlen: 80-120px</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section Visibility */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Sichtbare Sektionen
            </CardTitle>
            <CardDescription>
              Wählen Sie aus, welche Abschnitte im PDF angezeigt werden sollen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {sectionToggles.map((section) => (
                <div key={section.key} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <Label className="font-medium">{section.label}</Label>
                    <p className="text-sm text-muted-foreground">{section.description}</p>
                  </div>
                  <Switch
                    checked={settings[section.key] !== false}
                    onCheckedChange={(v) => updateSetting(section.key, v)}
                    data-testid={`pdf-${section.key}`}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Text Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Type className="h-4 w-4" />
              Texte & Hinweise
            </CardTitle>
            <CardDescription>
              Benutzerdefinierte Texte für verschiedene Bereiche des PDFs.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Abschlusstext (HTML)</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Dieser Text wird am Ende des PDFs vor dem Footer angezeigt. Sie können einfaches HTML verwenden 
                (z.B. &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;br/&gt;).
              </p>
              <Textarea
                value={currentSettings.closing_html}
                onChange={(e) => updateSetting('closing_html', e.target.value)}
                placeholder="<p>Vielen Dank für Ihr Interesse...</p>"
                rows={6}
                className="font-mono text-sm"
                data-testid="pdf-closing-html"
              />
            </div>
            
            <Separator />
            
            <div className="space-y-2">
              <Label>Benutzerdefinierter Footer-Text</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Überschreibt den automatisch generierten Footer-Text. Leer lassen für Standard.
              </p>
              <Input
                value={currentSettings.footer_text}
                onChange={(e) => updateSetting('footer_text', e.target.value)}
                placeholder="Ihr individueller Footer-Text"
                data-testid="pdf-footer-text"
              />
            </div>
          </CardContent>
        </Card>

        {/* Preview Info */}
        <Card className="bg-slate-100 border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-slate-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-sm">Vorschau</h4>
                <p className="text-sm text-muted-foreground">
                  Um die Änderungen zu sehen, erstellen Sie eine neue Analyse und laden Sie das PDF herunter.
                  Bestehende PDFs werden nicht automatisch aktualisiert.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button (Bottom) */}
        <div className="flex justify-end pt-4">
          <Button onClick={saveSettings} disabled={saving} size="lg" className="gap-2" data-testid="save-pdf-settings-bottom">
            <Save className="h-4 w-4" />
            {saving ? 'Speichern...' : 'Einstellungen speichern'}
          </Button>
        </div>
      </div>
    </div>
  );
}
